import { useCallback, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { fetchCurrentAdmin, getErrorsCount, getOrderErrorsCount, setLoading } from '../actions'
import {
  setAuthStatusAction,
  updateAuthAction,
  setAuthTokenAction,
  IndexedDBService,
  setCurrentAdminIdAction,
  clearAuthAction
} from '../redux/storeConfig/slices/slice.auth'

const broadcastChannel = new BroadcastChannel('storageSync')

/**
 * Custom hook to manage user session-related logic, including token validation,
 * admin login context, and redirection based on session state.
 *
 */
export default function useSession() {
  /** @type {import('../redux/storeConfig/slices/slice.auth/auth.types').AuthSlice['token']} */
  const session = useSelector(state => state.auth.token)
  const dispatch = useDispatch()

  /**
   * Displays an error message to the user.
   */
  // const handleError = useCallback(
  //   /**
  //    * @param {string} message - The error message to display.
  //    */
  //   message => {
  //     toastr.error('Error', message)
  //   },
  //   []
  // )

  /**
   * Validates the session token stored in the browser's session storage.
   */
  const checkStorageToken = useCallback(async () => {
    try {
      dispatch(updateAuthAction({ status: 'loading', task: 'fetchStorageToken' }))
      // dispatch(setLoading({ admin: true }))

      const sessionStorageToken = sessionStorage.getItem('agencyAuthToken')
      const indexedDbToken = await IndexedDBService.loadAgencyAuthToken()

      // const decodedIndexedDbToken = decodeToken(indexedDbToken)
      // const decodedSessionStorageToken = decodeToken(sessionStorageToken)

      const storageToken = sessionStorageToken ?? indexedDbToken ?? ''

      if (!storageToken) throw new Error('No token found in session storage.')

      localStorage.setItem('initialAuth', true)

      dispatch(setAuthTokenAction(storageToken))
      if (storageToken.length === 0) dispatch(setCurrentAdminIdAction(null))
      dispatch(setAuthStatusAction('succeeded'))

      return storageToken
    } catch (err) {
      dispatch(updateAuthAction({ status: 'failed', tokenVerified: false, currentAdminId: null }))
    }
  }, [dispatch])

  // const checkStorageToken = useCallback(async () => {
  //   try {
  //     dispatch(updateAuthAction({ status: 'loading', task: 'fetchStorageToken' }))

  //     const externalToken = await IndexedDBService.loadAgencyAuthToken()
  //     const sessionToken = sessionStorage.getItem('agencyAuthToken') ?? ''

  //     let favoredToken = externalToken || sessionToken // Default to whichever exists

  //     if (externalToken && sessionToken) {
  //       const decodedExternalToken = decodeToken(externalToken)
  //       const decodedSessionToken = decodeToken(sessionToken)

  //       const externalIsAgency = !!decodedExternalToken.agency_id
  //       const sessionIsAgency = !!decodedSessionToken.agency_id

  //       // If both tokens are agency tokens but different agencies, enforce only one
  //       if (externalIsAgency && sessionIsAgency && decodedExternalToken.agency_id !== decodedSessionToken.agency_id) {
  //         favoredToken = decodedExternalToken.exp > decodedSessionToken.exp ? externalToken : sessionToken
  //       } else {
  //         // Otherwise, just use the token with the later expiration
  //         favoredToken = decodedExternalToken.exp > decodedSessionToken.exp ? externalToken : sessionToken
  //       }
  //     }

  //     if (!favoredToken) throw new Error('No valid token found.')

  //     localStorage.setItem('initialAuth', true)

  //     // Prevent unnecessary Redux updates by checking if token is actually changing
  //     if (favoredToken !== session) {
  //       dispatch(setAuthTokenAction(favoredToken))
  //     }

  //     dispatch(setAuthStatusAction('succeeded'))
  //     return favoredToken
  //   } catch (err) {
  //     dispatch(updateAuthAction({ tokenVerified: false, currentAdminId: null, status: 'failed' }))
  //     dispatch(setAuthErrorsAction({ message: err.message }))
  //   }
  // }, [dispatch, session])

  /**
   * Validates the session token stored in the browser's session storage.
   */
  const handleAdminValidation = useCallback(async () => {
    try {
      if (!session) return

      dispatch(setLoading({ admin: true }))
      dispatch(updateAuthAction({ status: 'loading', task: 'verifyStorageToken' }))

      if (session === 'null') throw new Error('No token found in session storage.')

      /** @type {import('../reducers/adminsReducer').CurrentAdmin} */
      const adminData = await dispatch(fetchCurrentAdmin())

      if (adminData.logged_in_as === 'Organization') {
        await dispatch(getErrorsCount())
        await dispatch(getOrderErrorsCount())
      }

      if (adminData?.is_super_admin) sessionStorage.setItem('admin_permissions', 'false')
      else sessionStorage.setItem('admin_permissions', adminData?.admin_permissions)

      dispatch(updateAuthAction({ tokenVerified: !!adminData, currentAdminId: adminData?.id, status: 'succeeded' }))

      dispatch(setLoading({ admin: false }))
    } catch (err) {
      dispatch(
        updateAuthAction({
          status: 'failed',
          tokenVerified: false,
          currentAdminId: null,
          errors: [{ message: err.message, action: 'verifyStorageToken' }]
        })
      )

      dispatch(setLoading({ admin: false }))
    }
  }, [dispatch, session])

  // useEffect(() => {
  //   window.addEventListener('visibilitychange', checkStorageToken)

  //   return () => {
  //     window.removeEventListener('visibilitychange', checkStorageToken)
  //   }
  // }, [checkStorageToken])

  useEffect(() => {
    /**
     *
     * @param {MessageEvent} e
     */
    const handleBroadcastMessage = e => {
      if (e.data.type === 'logout') {
        dispatch(setAuthTokenAction(null))
      }
    }

    broadcastChannel.addEventListener('message', handleBroadcastMessage)

    const handleVisibilityChange = async () => {
      try {
        const sessionStorageToken = sessionStorage.getItem('agencyAuthToken')
        const indexedDbToken = await IndexedDBService.loadAgencyAuthToken()

        if (!sessionStorageToken && !indexedDbToken) dispatch(clearAuthAction())
        // // Start of multiple agency restriction
        // else if (sessionStorageToken && indexedDbToken) {
        //   const decoded = {
        //     session: decodeToken(sessionStorageToken),
        //     indexed: decodeToken(indexedDbToken)
        //   }
        //   if (
        //     decoded.session.agency_id &&
        //     decoded.indexed.agency_id &&
        //     decoded.session.agency_id !== decoded.indexed.agency_id
        //   ) {
        //     const yougestToken = decoded.session.exp > decoded.indexed.exp ? sessionStorageToken : indexedDbToken
        //     dispatch(setAuthTokenAction(yougestToken))
        //   }
        //   // End of multiple agency restriction
        // }
        else dispatch(setAuthTokenAction(sessionStorageToken ?? indexedDbToken ?? ''))
      } catch (err) {
        dispatch(updateAuthAction({ status: 'failed', tokenVerified: false, currentAdminId: null }))
      }
    }

    window.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      broadcastChannel.removeEventListener('message', handleBroadcastMessage)
      window.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [dispatch])

  return { checkStorageToken, handleAdminValidation }
}
